import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import type { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from '~/components/atoms/Link';
import { FORGOT_PASSWORD_PATH } from '~/constants';
import type { LoginFormType } from '~/types/Login';
import { LoginFormSchema } from '~/types/Login';
import type { LoginFormProps } from './LoginForm.types';

export const LoginForm: FC<LoginFormProps> = ({ onSubmit, isPending }) => {
  const { handleSubmit, control } = useForm<LoginFormType>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(LoginFormSchema),
  });

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} maxWidth={400} mx="auto">
      <Stack spacing={4.75} mb={2}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box>
              <TextField {...field} error={!!error} label="Email Address" variant="outlined" size="medium" fullWidth />
              {error && error.message ? (
                <Box px={1.75} pt={0.375}>
                  <FormHelperText error>{error.message}</FormHelperText>
                </Box>
              ) : null}
            </Box>
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box>
              <TextField
                {...field}
                error={!!error}
                label="Password"
                variant="outlined"
                size="medium"
                type="password"
                fullWidth
              />
              {error && error.message ? (
                <Box px={1.75} pt={0.375}>
                  <FormHelperText error>{error.message}</FormHelperText>
                </Box>
              ) : null}
            </Box>
          )}
        />
      </Stack>

      <Typography
        component={Link}
        id="login_forgot_password_link"
        variant="body2"
        fontWeight={600}
        to={FORGOT_PASSWORD_PATH}
        display="block"
        mb={4.75}
      >
        Forgot password
      </Typography>

      <Button id="login_submit_button" size="large" color="primary" variant="contained" type="submit" fullWidth>
        {isPending ? 'Logging in...' : 'Log in'}
      </Button>
    </Box>
  );
};
